import { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { ATTACH_BASE, THUMB_BASE } from "./utils";

export { ATTACH_BASE, THUMB_BASE };

const isImage = (p="") => /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(p);
const isPDF   = (p="") => /\.pdf$/i.test(p);
export const fileName = (p="") => p.split("/").pop();

// Full attachment URL (not thumbnail)
export const attachUrl = (filepath) => ATTACH_BASE + filepath;
// Thumbnail URL
export const thumbUrl  = (filepath) => THUMB_BASE  + fileName(filepath);

// ─── Image Lightbox ───────────────────────────────────────────────────────────
function Lightbox({ src, alt, onClose, all=[], currentIdx=0, onNav }) {
  const handleKey = useCallback(e => {
    if (e.key==="Escape") onClose();
    if (e.key==="ArrowRight" && onNav) onNav(1);
    if (e.key==="ArrowLeft"  && onNav) onNav(-1);
  },[onClose, onNav]);

  useEffect(()=>{
    document.addEventListener("keydown", handleKey);
    return ()=>document.removeEventListener("keydown", handleKey);
  },[handleKey]);

  return ReactDOM.createPortal(
    <div onClick={onClose}
      style={{ position:"fixed",inset:0,zIndex:10000,
        background:"rgba(0,0,0,0.92)",
        display:"flex",alignItems:"center",justifyContent:"center" }}>

      {/* Close */}
      <button onClick={onClose}
        style={{ position:"absolute",top:"16px",right:"20px",
          width:"36px",height:"36px",borderRadius:"50%",border:"none",
          background:"rgba(255,255,255,0.15)",color:"#fff",
          fontSize:"20px",cursor:"pointer",lineHeight:1,
          display:"flex",alignItems:"center",justifyContent:"center" }}>×</button>

      {/* Prev */}
      {all.length>1 && currentIdx>0 && (
        <button onClick={e=>{e.stopPropagation();onNav(-1);}}
          style={{ position:"absolute",left:"16px",
            width:"40px",height:"40px",borderRadius:"50%",border:"none",
            background:"rgba(255,255,255,0.15)",color:"#fff",
            fontSize:"22px",cursor:"pointer",lineHeight:1 }}>‹</button>
      )}

      {/* Image */}
      <div onClick={e=>e.stopPropagation()}
        style={{ maxWidth:"90vw",maxHeight:"90vh",position:"relative" }}>
        <img src={src} alt={alt||""}
          style={{ maxWidth:"90vw",maxHeight:"88vh",objectFit:"contain",
            borderRadius:"8px",boxShadow:"0 20px 60px rgba(0,0,0,0.5)",
            display:"block" }}/>
        {/* Caption */}
        {alt && <div style={{ textAlign:"center",color:"rgba(255,255,255,0.6)",
          fontSize:"12px",marginTop:"10px" }}>{alt}</div>}
        {/* Counter */}
        {all.length>1 && <div style={{ textAlign:"center",color:"rgba(255,255,255,0.4)",
          fontSize:"11px",marginTop:"4px" }}>{currentIdx+1} / {all.length}</div>}
      </div>

      {/* Next */}
      {all.length>1 && currentIdx<all.length-1 && (
        <button onClick={e=>{e.stopPropagation();onNav(1);}}
          style={{ position:"absolute",right:"16px",
            width:"40px",height:"40px",borderRadius:"50%",border:"none",
            background:"rgba(255,255,255,0.15)",color:"#fff",
            fontSize:"22px",cursor:"pointer",lineHeight:1 }}>›</button>
      )}
    </div>,
    document.body
  );
}

// ─── useFileViewer hook ───────────────────────────────────────────────────────
// Returns: { lightbox, openFile(filepath), openImage(filepath, allImages) }
export function useFileViewer() {
  const [lb, setLb] = useState(null); // { src, alt, all, idx }

  const openFile = (filepath) => {
    const url = attachUrl(filepath);
    if (isImage(filepath)) {
      setLb({ src:url, alt:fileName(filepath), all:[filepath], idx:0 });
    } else {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const openImage = (filepath, allImages=[]) => {
    const idx = allImages.indexOf(filepath);
    setLb({
      src:   attachUrl(filepath),
      alt:   fileName(filepath),
      all:   allImages,
      idx:   Math.max(0, idx),
    });
  };

  const closeLb = () => setLb(null);
  const navLb   = (dir) => {
    if (!lb) return;
    const next = lb.idx + dir;
    if (next<0 || next>=lb.all.length) return;
    setLb({ ...lb, idx:next, src:attachUrl(lb.all[next]), alt:fileName(lb.all[next]) });
  };

  const lightbox = lb ? (
    <Lightbox src={lb.src} alt={lb.alt}
      all={lb.all} currentIdx={lb.idx}
      onClose={closeLb} onNav={navLb}/>
  ) : null;

  return { lightbox, openFile, openImage };
}

// ─── FilePill component — clickable ───────────────────────────────────────────
export function FilePill({ filepath, onClick }) {
  const [hov, setHov] = useState(false);
  const name = fileName(filepath).replace(/_\d{2}_\d{2}_\d{4}_\d{2}_\d{2}\./,".");
  const ext  = name.split(".").pop().toUpperCase();
  const isImg= isImage(filepath);
  const ec   = isImg              ? "#059669"
             : ext==="PDF"        ? "#DC2626"
             : ext==="PPTX"||ext==="PPT" ? "#D97706"
             : ext==="DOCX"||ext==="DOC" ? "#2563EB"
             : ext==="XLSX"||ext==="XLS" ? "#059669"
             : "#6B7280";
  const em   = isImg?"🖼":ext==="PDF"?"📄":ext==="PPTX"||ext==="PPT"?"📊":
               ext==="DOCX"||ext==="DOC"?"📝":ext==="XLSX"?"📊":"📎";

  return (
    <div onClick={onClick}
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{ display:"inline-flex",alignItems:"center",gap:"5px",
        padding:"4px 10px",borderRadius:"7px",cursor:"pointer",
        background: hov?`${ec}10`:"#fff",
        border:`1px solid ${hov?ec:ec+"44"}`,
        fontSize:"11px",color:"#374151",flexShrink:0,
        transition:"all 0.12s",userSelect:"none" }}>
      <span>{em}</span>
      <span style={{ maxWidth:"160px",overflow:"hidden",textOverflow:"ellipsis",
        whiteSpace:"nowrap",fontWeight:"500" }}>{name}</span>
      <span style={{ background:ec,color:"#fff",borderRadius:"4px",
        padding:"1px 4px",fontSize:"8px",fontWeight:"800" }}>{ext}</span>
    </div>
  );
}

// ─── ImageThumb — clickable thumbnail ────────────────────────────────────────
export function ImageThumb({ filepath, allImages=[], style={}, onClick, fit="cover", bg="transparent", useFull=false }) {
  const [hov, setHov] = useState(false);
  const src = useFull ? attachUrl(filepath) : thumbUrl(filepath);
  return (
    <div onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      onClick={onClick}
      style={{ position:"relative",cursor:"pointer",overflow:"hidden",
        borderRadius:"8px",border:`2px solid ${hov?"#6366F1":"transparent"}`,
        background:bg,
        transition:"all 0.12s",...style }}>
      <img src={src} alt={fileName(filepath)}
        onError={e=>e.target.style.display="none"}
        style={{ width:"100%",height:"100%",objectFit:fit,display:"block" }}/>
      {hov && <div style={{ position:"absolute",inset:0,
        background:"rgba(99,102,241,0.25)",
        display:"flex",alignItems:"center",justifyContent:"center" }}>
        <span style={{ fontSize:"22px" }}>🔍</span>
      </div>}
    </div>
  );
}

// Helper: split chatbot into images + files
export function splitChatbot(chatbot=[]) {
  const imgs  = chatbot.filter(a=>isImage(a.FILEPATH));
  const files = chatbot.filter(a=>!isImage(a.FILEPATH));
  return { imgs, files };
}